import axios from 'axios'
import {useCallback, useEffect, useState} from 'react'
import {Form} from 'react-bootstrap'
import {useTranslation} from 'react-i18next'
import {TextInputV2} from 'src/components'
import {CustomModal} from 'src/components/CustomModal'
import {useRowTable} from 'src/hooks/useRowTable'
import {useAppDispatch, useAppSelector} from 'src/redux/hooks'
import {filterSelector, resetFilterCustomer, setFilterCustomer} from 'src/redux/slices/filterSlices'
import {objToParams} from 'src/utils'
import * as TableUI from '../Table/Table'
import {fetchCustomers, headers} from '../../services/fetcher'
import Pagination from '../../app/pages/component/Pagination'

interface Props {
  show: boolean
  selected: string
  onClose: () => void
  onSelectCustomer: (customerId: string) => void
}

export const ModalBrowseCustomers = ({show, selected, onClose, onSelectCustomer}: Props) => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const {filterCustomer: filter} = useAppSelector(filterSelector)

  const {RowEmptyData, RowErrorFetch, RowLoader} = useRowTable({
    colSpan: 4,
    onReloadTable: () => dispatch(resetFilterCustomer()),
  })

  const [isLoading, setIsLoading] = useState(false)
  const [customers, setCustomers] = useState([])
  const [_page, setPage] = useState(1)
  const [numberURL, setNumberURL] = useState([])
  const [isError, setIsError] = useState(false)

  const handleFilterChange = (value: Partial<typeof filter>) => {
    dispatch(setFilterCustomer({...filter, ...value}))
  }

  const onFetchCustomers = useCallback(() => {
    setIsLoading(true)

    fetchCustomers(filter)
      .then((res) => {
        const data = res.data.data
        setCustomers(data.data)
        setNumberURL(data.links)
        setPage(data.current_page)
      })
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false))
  }, [filter])

  const paginateNumber = async (url: string) => {
    if (!url) return

    try {
      setIsLoading(true)

      const token = localStorage.getItem('token') as string
      const endpoint = `${url}&${objToParams(filter)}`
      const res = await axios.get(endpoint, {
        headers: {...headers, Authorization: `Bearer ${token}`},
      })
      const data = res.data.data

      setCustomers(data.data)
      setNumberURL(data.links)
      setPage(data.current_page)
    } catch (error: any) {
      setIsError(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const getData = setTimeout(onFetchCustomers, 1000)
    return () => clearTimeout(getData)
  }, [onFetchCustomers])

  useEffect(() => {
    dispatch(resetFilterCustomer())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <CustomModal title={t('Browse')} show={show} centered size='lg' onHide={onClose}>
      <TableUI.TableWrapper>
        <TableUI.Table>
          <TableUI.THead>
            <tr>
              <th>&nbsp;</th>
              <th>{t('Customer Code')}</th>
              <th>{t('Name')}</th>
              <th>{t('Customer Account Group')}</th>
            </tr>
          </TableUI.THead>
          <TableUI.TBody>
            <TableUI.TRowFilter>
              <td>&nbsp;</td>
              <td>
                <TextInputV2
                  placeholder={t('Customer Code')}
                  value={filter?.customer_code ?? ''}
                  onChange={({target}) => handleFilterChange({customer_code: target.value})}
                />
              </td>
              <td>
                <TextInputV2
                  placeholder={t('Name')}
                  value={filter?.name ?? ''}
                  onChange={({target}) => handleFilterChange({name: target.value})}
                />
              </td>
              <td>
                <TextInputV2
                  placeholder={t('Customer Account Group')}
                  value={filter?.customer_account_group ?? ''}
                  onChange={({target}) =>
                    handleFilterChange({customer_account_group: target.value})
                  }
                />
              </td>
            </TableUI.TRowFilter>

            {isLoading ? (
              <RowLoader />
            ) : isError ? (
              <RowErrorFetch />
            ) : customers?.length === 0 ? (
              <RowEmptyData />
            ) : (
              customers?.map((item: any, index: number) => (
                <tr key={index}>
                  <td>
                    <Form.Check
                      type='checkbox'
                      id={item?.customer_code}
                      checked={item?.customer_code === selected}
                      onChange={(e: any) => onSelectCustomer(e.target.id)}
                    />
                  </td>
                  <td>{item.customer_code}</td>
                  <td>{`${item.title}. ${item.name}`}</td>
                  <td>{item.customer_account_group}</td>
                </tr>
              ))
            )}
          </TableUI.TBody>
        </TableUI.Table>
      </TableUI.TableWrapper>

      <div className='mt-5'>
        <Pagination paginate={paginateNumber} test={numberURL} />
      </div>
    </CustomModal>
  )
}
